import { calc } from 'csx';
import { type Styles } from 'react-modal';
import { cssRaw } from 'typestyle';
import { type CSSProperties } from 'typestyle/lib/types';
import StyleConstants from '../../../core/styles.constants';

export const modalStyleDatePicker = (isMobile?: boolean, width?: number): Styles => ({
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    boxShadow: '1px 2px 15px rgba(0, 0, 0, 0.25)',
    borderRadius: 8,
    width: width ?? 392,
    maxWidth: isMobile ? calc('100% - 30px') : undefined,
    maxHeight: calc('100% - 30px'),
    backgroundColor: StyleConstants.WHITE_COLOR,
  },
});

export const modalBodyDatePicker = (isMobile?: boolean): CSSProperties => ({
  padding: isMobile ? 0 : 24,
  wordBreak: 'break-word',
  maxHeight: '75%',
  maxWidth: '100%',
});

export const wrapperDiv: CSSProperties = {
  height: 500,
};

export const modalText: CSSProperties = {
  marginTop: 8,
  fontSize: StyleConstants.SECONDARY_FONT_SIZE,
  fontWeight: StyleConstants.CARD_BODY_FONT_WEIGHT,
  color: StyleConstants.GREY_COLOR,
};

export const columnHeaderDatePicker = (isMobile: boolean, marginBottom?: number): CSSProperties => ({
  wordBreak: 'break-word',
  textAlign: isMobile ? 'center' : undefined,
  fontSize: StyleConstants.HEADER_FONT_SIZE,
  fontWeight: StyleConstants.HEADER_FONT_WEIGHT,
  fontFamily: StyleConstants.HEADER_FONT_FAMILY,
  color: StyleConstants.GREY_COLOR,
  marginBottom,
});

export const error: CSSProperties = {
  color: StyleConstants.PINK_COLOR,
  marginTop: 12,
};

export const button: CSSProperties = {
  marginTop: 24,
};

export const deleteIcon: CSSProperties = {
  marginLeft: 8,
  height: 24,
  width: 24,
};

export const deleteButton: CSSProperties = {
  marginTop: 24,
  width: 150,
  cursor: 'pointer',
  wordBreak: 'break-word',
  fontSize: StyleConstants.CARD_BODY_FONT_SIZE,
  fontWeight: StyleConstants.CARD_BODY_FONT_WEIGHT,
  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
  color: StyleConstants.PINK_COLOR,
};

export const bodyText: CSSProperties = {
  marginBottom: 8,
  wordBreak: 'break-word',
  textAlign: 'center',
  fontSize: StyleConstants.CARD_TITLE_FONT_SIZE,
  fontWeight: StyleConstants.CARD_TITLE_FONT_WEIGHT,
  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
  color: StyleConstants.GREY_COLOR,
};

export const emptyListMessage: CSSProperties = {
  wordBreak: 'break-word',
  lineBreak: 'strict',
  fontSize: StyleConstants.CARD_BODY_FONT_SIZE,
  fontWeight: StyleConstants.CARD_BODY_FONT_WEIGHT,
  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
  color: StyleConstants.GREY_COLOR,
};

export const gridRow: CSSProperties = {
  marginTop: 12,
};

export const rowItem = (isLastItem = false): CSSProperties => ({
  marginBottom: isLastItem ? 0 : 12,
  width: '100%',
});

export const selectionModeButton = (isActive: boolean): CSSProperties => ({
  padding: '6px 16px',
  borderRadius: 4,
  border: `1px solid ${isActive ? StyleConstants.ORANGE_COLOR : StyleConstants.LIGHT_GREY_COLOR}`,
  backgroundColor: isActive ? StyleConstants.ORANGE_COLOR : StyleConstants.WHITE_COLOR,
  color: isActive ? StyleConstants.WHITE_COLOR : StyleConstants.GREY_COLOR,
  fontSize: StyleConstants.CARD_BODY_FONT_SIZE,
  fontWeight: StyleConstants.CARD_BODY_FONT_WEIGHT,
  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
  cursor: 'pointer',
});

export const disabledCalendarCell: CSSProperties = {
  fontSize: StyleConstants.CARD_BODY_FONT_SIZE,
  fontWeight: StyleConstants.CARD_BODY_FONT_WEIGHT,
  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
  color: `${StyleConstants.GREY_COLOR}50`,
  border: StyleConstants.CALENDAR_TILE_BORDER_SIZE,
  height: StyleConstants.CALENDAR_CELL_HEIGHT,
  background: `${StyleConstants.LIGHT_GREY_COLOR}50`,
};

export const todayCalendarCell: CSSProperties = {
  fontSize: StyleConstants.CARD_BODY_FONT_SIZE,
  fontWeight: StyleConstants.CARD_BODY_FONT_WEIGHT,
  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
  color: StyleConstants.ORANGE_COLOR,
  border: StyleConstants.CALENDAR_TILE_BORDER_SIZE,
  background: StyleConstants.WHITE_COLOR,
  textDecoration: 'underline',
};

export const defaultCalendarCell: CSSProperties = {
  fontSize: StyleConstants.CARD_BODY_FONT_SIZE,
  fontWeight: StyleConstants.CARD_BODY_FONT_WEIGHT,
  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
  color: StyleConstants.GREY_COLOR,
  border: StyleConstants.CALENDAR_TILE_BORDER_SIZE,
  background: StyleConstants.WHITE_COLOR,
};

export const divider: CSSProperties = {
  marginLeft: 4,
  marginRight: 4,
};

export const calendar: CSSProperties = {
  fontWeight: StyleConstants.CARD_BODY_FONT_WEIGHT,
  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
  marginTop: 15,
  marginBottom: 15,
  width: '400px',
  height: '340px',
  border: '0px',
};

export const horizontalDivider: CSSProperties = {
  opacity: 0.5,
  height: 1,
  backgroundColor: 'rgba(36, 55, 75, 0.2)',
  marginBottom: 10,
  marginTop: 5,
};

cssRaw(
  `
  .react-calendar__month-view__weekdays {
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: grey;
  }

  div.react-calendar__month-view__weekdays__weekday abbr {
    text-decoration: none;
  }

  .react-calendar__navigation__label{
    color: grey;
  }

  .react-calendar__navigation{
    height: 40px;
    border: 0px;
  }

  .react-calendar__tile {
    height: 40px;
    position: relative;
  }

  .react-calendar__tile abbr {
    position: relative;
    z-index: 1;
  }

  .react-calendar__navigation__arrow {
    width: 30px;
    color: grey;
    border: 0px;
  }

  button[class*="existing_entry_calendar_cell"] {
    background-color: white;
    border: 0px;
  }

  button[class*="existing_entry_calendar_cell"] abbr {
    color: white;
  }

  button[class*="existing_entry_today_calendar_cell"] abbr {
    color: orange;
    text-decoration: underline;
  }

  .react-calendar__tile:disabled {
    background-color: white;
  }

  button[class*="existing_entry_calendar_cell"] abbr::before {
    content: "";
    position: absolute;
    display: block;
    border-radius: 50%;
    background-color: #328BE0;
    width: 34px;
    height: 34px;
    left: 50%;
    top: 50%;
    transform: translate(-17px, -17px);
    z-index: -1
  }

  button[class*="existing_entry_today_calendar_cell"] abbr::before {
    content: "";
    position: absolute;
    display: block;
    border-radius: 50%;
    background-color: #328BE0;
    width: 34px;
    height: 34px;
    left: 50%;
    top: 50%;
    transform: translate(-17px, -17px);
    z-index: -1
  }

  .react-calendar__tile--now:enabled:hover, .react-calendar__tile--now:hover {
    background-color: #e6e6e6;
  }

  .react-date-picker .react-calendar__tile--now:enabled, .react-calendar__tile--now {
    color: #FFB63E;
  }

  .react-calendar__tile--range {
    background-color: rgba(255, 182, 62, 0.15);
  }

  .react-calendar__tile--rangeStart,
  .react-calendar__tile--rangeEnd {
    background-color: #FFB63E;
    color: white;
  }

  .react-calendar__tile--rangeStart abbr,
  .react-calendar__tile--rangeEnd abbr {
    color: white;
  }
  `,
);
